import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { useNavigate, useLocation } from 'react-router-dom';
import { get } from '@/features/shared/lib/api';

/** id가 숫자만으로 이루어졌는지 (예: "893") */
function isNumericId(id) {
  if (id == null) return false;
  return /^\d+$/.test(String(id).trim());
}

/**
 * ForceNetwork
 * - Reusable force-directed network chart (D3) with zoom/pan.
 * - Props (two usage modes):
 *   1) Data-driven: pass nodes and links directly.
 *      - nodes: [{ id: string|number, label?: string, center?: boolean }]
 *      - links: [{ source: string|number, target: string|number, weight?: number }]
 *   2) Simple mock: pass `record` and the component will generate a default network
 *      using a single unified array where the first element is the center node.
 *   - height: number (optional, default 420)
 *   - highlightedLinkColor: string (optional)
 */
export default function ForceNetwork({
  nodes = undefined,
  links = undefined,
  record,
  height = 420,
  highlightedLinkColor = '#b6c3dc',
}) {
  const ref = useRef(null);
  const modalRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [apiData, setApiData] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);

  // API에서 데이터 가져오기
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  // 다른 페이지/다른 연구자로 이동 시 전체 보기·body 스크롤 잠금 해제 (같은 라우트에서도 언마운트 없이 대응)
  const scholarRouteKey = record?.scholar_id ?? record?.id;
  useEffect(() => {
    setIsFullscreen(false);
  }, [location.pathname, scholarRouteKey]);

  // API에서 데이터 가져오기
  useEffect(() => {
    if (!record?.scholar_id && !record?.id) return;

    let alive = true;
    (async () => {
      setApiLoading(true);
      try {
        const scholarId = record?.scholar_id || record?.id;
        const data = await get(`/rm/v1/researcher/${encodeURIComponent(scholarId)}/force_network`);
        if (!alive) return;
        if (data?.status && data?.result) {
          setApiData(data.result);
        }
      } catch (e) {
        console.error('[ForceNetwork] API error:', e);
      } finally {
        if (alive) setApiLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [record?.scholar_id, record?.id]);

  // Build default nodes/links when not provided, using a single array (center first)
  const { finalNodes, finalLinks } = useMemo(() => {
    // 1. 직접 전달된 nodes/links가 있으면 우선 사용
    if (Array.isArray(nodes) && Array.isArray(links) && nodes.length && links.length) {
      return { finalNodes: nodes, finalLinks: links };
    }

    // 2. API에서 가져온 데이터가 있으면 사용
    if (apiData && Array.isArray(apiData.nodes) && Array.isArray(apiData.links)) {
      return { finalNodes: apiData.nodes, finalLinks: apiData.links };
    }

    // 데이터가 없으면 null 반환
    return { finalNodes: null, finalLinks: null };
  }, [nodes, links, record, apiData]);

  useEffect(() => {
    const el = ref.current;
    const modalEl = modalRef.current;
    if (!el && !modalEl) return;

    // 데이터가 없으면 렌더링하지 않음
    if (!finalNodes || !finalLinks || finalNodes.length === 0 || finalLinks.length === 0) {
      return;
    }

    const targetEl = isFullscreen ? modalEl : el;
    if (!targetEl) return;
    targetEl.innerHTML = '';

    const width = targetEl.clientWidth || (isFullscreen ? window.innerWidth * 0.9 : 800);
    const h = isFullscreen ? 600 : height;

    const svg = d3
      .select(targetEl)
      .append('svg')
      .attr('width', '100%')
      .attr('height', h)
      .attr('viewBox', `0 0 ${width} ${h}`)
      .style('cursor', 'grab');

    const g = svg.append('g');

    // zoom & pan
    const zoom = d3
      .zoom()
      .scaleExtent([0.2, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Initial center
    svg.call(zoom.transform, d3.zoomIdentity.translate(width / 2, h / 2).scale(0.8));

    // Color scales
    const linkColor = '#CCCCCC';
    const nodeStroke = '#aaa';
    const nodeFill = '#ddd';
    const centerFill = '#0FB1B8';

    // Build simulation
    const initialSimNodes = finalNodes.map(n => {
      const newNode = { ...n };
      if (n.center) {
        newNode.x = 0;
        newNode.y = 0;
        newNode.fx = 0;
        newNode.fy = 0;
      }
      return newNode;
    });

    const sim = d3
      .forceSimulation(initialSimNodes)
      .force('link', d3.forceLink(finalLinks.map(l => ({ ...l })))
        .id(d => d.id)
        // 가중치(협업 횟수)가 높을수록 선의 길이를 짧게 (더 친밀하게)
        .distance(d => Math.max(40, 150 - (d.weight * 15))))
      .force('charge', d3.forceManyBody().strength(-400)) // 노드 간 반발력 강화
      .force('center', d3.forceCenter(0, 0))
      .force('collision', d3.forceCollide().radius(45)); // 라벨이 겹치지 않게 반지름 확보

    // Draw links
    const link = g
      .append('g')
      .attr('stroke', linkColor)
      .attr('stroke-opacity', 0.8)
      .selectAll('line')
      .data(sim.force('link').links())
      .enter()
      .append('line')
      .attr('stroke-width', d => Math.max(1, Math.min(8, d.weight || 1)))
      .attr('stroke', d => (d.weight && d.weight >= 8 ? highlightedLinkColor : linkColor));

    // Draw nodes
    const node = g
      .append('g')
      .selectAll('g.node')
      .data(sim.nodes())
      .enter()
      .append('g')
      .attr('class', 'node')
      .call(
        d3
          .drag()
          .on('start', (event, d) => {
            if (!event.active) sim.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on('drag', (event, d) => {
            d.fx = event.x;
            d.fy = event.y;
          })
          .on('end', (event, d) => {
            if (!event.active) sim.alphaTarget(0);
            if (!d.center) {
              d.fx = null;
              d.fy = null;
            }
          }),
      );

    node
      .append('circle')
      .attr('r', d => (d.center ? 10 : 7))
      .attr('fill', d => (d.center ? centerFill : nodeFill))
      .attr('stroke', nodeStroke)
      .attr('stroke-width', d => (d.center ? 2 : 1))
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        const svgNode = svg.node();
        const transform = d3.zoomTransform(svgNode);
        // SVG 좌표를 컨테이너 기준 상대 좌표로 변환
        const x = (d.x * transform.k) + transform.x;
        const y = (d.y * transform.k) + transform.y;
        setPopupData({ name: d.label ?? d.id, count: d.count ?? 0, id: d.id });
        setPopupPosition({ x, y });
      });

    node
      .append('text')
      .text(d => d.label ?? d.id)
      .attr('x', 12)
      .attr('y', 4)
      .attr('fill', '#2A2E36')
      .style('font-size', 12)
      .style('font-weight', d => (d.center ? 800 : 600))
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        const svgNode = svg.node();
        const transform = d3.zoomTransform(svgNode);
        // SVG 좌표를 컨테이너 기준 상대 좌표로 변환
        const x = (d.x * transform.k) + transform.x;
        const y = (d.y * transform.k) + transform.y;
        setPopupData({ name: d.label ?? d.id, count: d.count ?? 0, id: d.id });
        setPopupPosition({ x, y });
      });

    sim.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    // SVG 외부 클릭 시 팝업 닫기
    const handleOutsideClick = (e) => {
      if (isFullscreen) {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
          setPopupData(null);
        }
      } else {
        if (ref.current && !ref.current.contains(e.target)) {
          setPopupData(null);
        }
      }
    };

    document.addEventListener('click', handleOutsideClick);

    // 팝업 열릴 때 줌 초기화
    if (isFullscreen) {
      setTimeout(() => {
        svg.transition()
          .duration(500)
          .call(zoom.transform, d3.zoomIdentity.translate(width / 2, h / 2).scale(0.8));
      }, 100);
    }

    return () => {
      try { sim.stop(); } catch (e) { }
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [finalNodes, finalLinks, height, highlightedLinkColor, isFullscreen]);

  const renderPopup = () => {
    if (!popupData) return null;
    const hasLink = popupData.id != null && isNumericId(popupData.id);
    return (
      <div
        className="absolute z-[10000] bg-white border border-[#E4E8F1] rounded-lg shadow-lg p-4 min-w-[160px] pr-2 cursor-pointer hover:shadow-xl transition-shadow"
        style={{
          left: `${popupPosition.x}px`,
          top: `${popupPosition.y}px`,
          transform: 'translate(-50%, -100%)',
          marginTop: '-8px',
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (hasLink) {
            try {
              sessionStorage.setItem('researcher-back-restore', JSON.stringify({
                path: window.location.pathname + window.location.search,
                scrollY: typeof window !== 'undefined' ? window.scrollY : 0,
              }));
            } catch (err) {}
            setIsFullscreen(false);
            if (typeof document !== 'undefined') {
              document.body.style.overflow = '';
            }
            navigate(`/researchers/${popupData.id}`);
          }
        }}
      >
        <div className="text-[14px] font-semibold mb-1 transition-colors flex items-center gap-2">
          <span className={`min-w-0 flex-1 ${hasLink ? 'underline text-[#376BDB] hover:text-[#2952a3]' : 'text-[#2A2E36]'}`}>
            {popupData.name}
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              setPopupData(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setPopupData(null);
              }
            }}
            className="inline-flex items-center justify-center w-8 h-8 text-[16px] leading-none text-[#8D9AAF] rounded-full hover:bg-[#EEF1F8] hover:text-[#2A2E36] transition-colors cursor-pointer flex-shrink-0"
          >
            ×
          </span>
        </div>
        <div className="text-[12px] text-[#6C7687] space-y-0.5">
          <div>Count: <span className="font-semibold">{popupData.count}</span></div>
        </div>
      </div>
    );
  };

  // 로딩 상태 처리
  if (apiLoading) {
    return (
      <div className="flex items-center justify-center min-h-[420px] text-[#8D9AAF]">
        Loading...
      </div>
    );
  }

  // 데이터가 없는 경우
  if (!finalNodes || !finalLinks || finalNodes.length === 0 || finalLinks.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[420px] text-[#8D9AAF]">
        데이터가 없습니다.
      </div>
    );
  }

  return (
    <>
      <div className="w-full relative group">
        <div ref={ref} className="w-full" />
        
        {/* 크게 보기 버튼 */}
        <button
          type="button"
          onClick={() => setIsFullscreen(true)}
          className="absolute top-4 right-4 z-30 w-10 h-10 bg-white border border-[#E4E8F1] rounded-lg flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
          title="크게 보기"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 3H21V9" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 21H3V15" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M21 3L14 10" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 21L10 14" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {!isFullscreen && renderPopup()}
      </div>

      {/* 레이어 팝업 (모달) */}
      {isFullscreen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsFullscreen(false)}
          />
          <div className="relative w-full h-full lg:w-[90%] lg:h-[700px] bg-white lg:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E4E8F1]">
              <h3 className="text-[18px] font-bold text-[#2A2E36]">Network Visualization (Full View)</h3>
              <button
                type="button"
                onClick={() => setIsFullscreen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 14H10V20" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M20 10H14V4" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M14 10L21 3" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M10 14L3 21" stroke="#6C7687" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            <div className="flex-1 relative bg-white overflow-hidden">
              <div ref={modalRef} className="w-full h-full" />
              {isFullscreen && renderPopup()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
